import { Type } from 'class-transformer';
import { IsInt, IsOptional, IsString, Max, MaxLength, Min } from 'class-validator';

export const RECALL_SEARCH_DEFAULT_PAGE_SIZE = 20;
export const RECALL_SEARCH_MAX_PAGE_SIZE = 50;

/** Mirror of the Prisma `RecallExtractionConfidence` enum — shared cannot depend on `@prisma/client`. */
export const RECALL_EXTRACTION_CONFIDENCES = ['HIGH', 'MEDIUM', 'LOW'] as const;
export type RecallExtractionConfidence = (typeof RECALL_EXTRACTION_CONFIDENCES)[number];

/**
 * Query for the "Ministry recall already imported" AI-fill source (M0). Searches only
 * PUBLISHED RECALL items that carry a structured `RecallDetail` — read path is new,
 * distinct from the `{brands, lotNumbers, eans}` projection used by tenant-recall matching.
 */
export class RecallSearchQueryDto {
  @IsOptional()
  @IsString()
  @MaxLength(200)
  q?: string;

  @IsOptional()
  @Type(() => Number)
  @IsInt()
  @Min(1)
  page?: number;

  @IsOptional()
  @Type(() => Number)
  @IsInt()
  @Min(1)
  @Max(RECALL_SEARCH_MAX_PAGE_SIZE)
  pageSize?: number;
}

/**
 * One published Ministry recall, with the structured evidence the AI-fill form maps
 * directly. Single source of truth for the shape the backend response and the frontend
 * consumer both agree on: `warnings` mirrors `RecallDetail.warnings` — one free-text note
 * or `null`, never an array — and `extractionConfidence` is the three-value enum the
 * database stores, never a numeric score. The backend's Swagger-decorated
 * `RecallSearchItemDto`/`RecallSearchResponseDto` classes (`backend/src/regulatory-news/dto/recall-search.dto.ts`)
 * `implements` these interfaces so a drift between the two sides fails `tsc`, not just a
 * human review.
 */
export interface RecallSearchItemDto {
  newsItemId: string;
  title: string;
  /** When Prospero promoted the item into the weekly digest (identical for the whole batch). */
  publishedAt: string | null;
  /**
   * Publication date declared by the source itself (the ministry act), when it reports one.
   * Null = the source has no date: consumers fall back to `publishedAt`.
   */
  sourcePublishedAt: string | null;
  productNames: string[];
  brands: string[];
  lotNumbers: string[];
  producer: string | null;
  hazard: string | null;
  warnings: string | null;
  extractionConfidence: RecallExtractionConfidence;
  sourceDocumentUrl: string | null;
}

export interface RecallSearchResponseDto {
  items: RecallSearchItemDto[];
  total: number;
  page: number;
  pageSize: number;
}
